import { Input, Select } from '@aigc/antd';
import { Datetime } from '@aigc/components';
import { usePagingQueryHelper } from '@aigc/hooks';
import { useOverlay } from '@aigc/overlay-hook';
import { PlusOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  App,
  Button,
  Form,
  Image,
  Space,
  Table,
  Tag,
  type TableColumnsType,
} from 'antd';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { match } from 'ts-pattern';
import { array, object, string } from 'yup';
import { FormControl } from '../../../components/form-control';
import {
  deleteAigcAsset,
  queryAigcAssets,
  type AssetQueryParams,
} from '../api';
import type { AssetDto } from '../model';
import { AssetCreateModal } from './asset-create-modal';
import { AssetEditModal } from './asset-edit-modal';

const statusColorMap: Record<string, string> = {
  active: 'green',
  processing: 'blue',
  failed: 'red',
};

export function AssetTable() {
  const { modal, message } = App.useApp();
  const { control, handleSubmit } = useForm({
    resolver: yupResolver(
      object({
        name: string().nullable(),
        status: array().of(string().required()).nullable(),
      }),
    ),
  });
  const { pagination, queryParams, queryMany, changePageNumber } =
    usePagingQueryHelper<AssetQueryParams>({
      defaultQueryParams: { status: [] },
      onRefresh: () => {
        refetch();
      },
    });

  const { data, isFetching, refetch } = useQuery({
    ...queryAigcAssets(pagination, queryParams),
    placeholderData: keepPreviousData,
  });

  const { mutateAsync: doDelete } = useMutation({
    mutationKey: queryAigcAssets.BASE_QUERY_KEY,
    mutationFn: deleteAigcAsset,
  });

  const [openOverlay] = useOverlay();

  const openCreate = useCallback(() => {
    const ref = openOverlay(<AssetCreateModal />);
    ref.afterClosed.then(() => refetch());
  }, [openOverlay, refetch]);

  const openEdit = useCallback(
    (row: AssetDto) => {
      const ref = openOverlay(<AssetEditModal row={row} />);
      ref.afterClosed.then(() => refetch());
    },
    [openOverlay, refetch],
  );

  const handleDelete = useCallback(
    (row: AssetDto) => {
      modal.confirm({
        title: '确认删除',
        content: `确定要删除资产 "${row.name}" 吗？删除后不可恢复。`,
        okText: '删除',
        okButtonProps: { variant: 'filled', color: 'danger' },
        cancelText: '取消',
        onOk: async () => {
          try {
            await doDelete(row.id);
            message.success('删除成功');
            refetch();
          } catch {
            message.error('删除失败');
          }
        },
      });
    },
    [modal, doDelete, message, refetch],
  );

  const columns = useMemo<TableColumnsType<AssetDto>>(
    () => [
      {
        title: '图片',
        width: 80,
        render: (_, row) => (
          <Image
            src={row.filename ?? ''}
            width={64}
            height={64}
            classNames={{ image: 'object-cover' }}
          ></Image>
        ),
      },
      {
        title: '人物名称',
        render: (_, row) => row.name,
      },
      {
        title: '状态',
        width: 100,
        render: (_, row) => {
          const status = row.status ?? 'unknown';
          return (
            <Tag color={statusColorMap[status] ?? 'default'}>
              {match(status)
                .with('active', () => '已激活')
                .with('processing', () => '处理中')
                .with('failed', () => '失败')
                .otherwise(() => status)}
            </Tag>
          );
        },
      },
      {
        title: '创建人',
        width: 180,
        render: (_, row) => {
          return row.creator?.name;
        },
      },
      {
        title: '创建时间',
        width: 180,
        render: (_, row) => {
          return <Datetime value={row.created_at}></Datetime>;
        },
      },
      {
        title: '更新时间',
        width: 180,
        render: (_, row) => {
          return <Datetime value={row.updated_at}></Datetime>;
        },
      },
      {
        title: '操作',
        width: 140,
        render: (_, row) => (
          <Space size={8}>
            <Button
              variant="filled"
              color="default"
              size="small"
              onClick={() => openEdit(row)}
            >
              编辑
            </Button>
            <Button
              variant="filled"
              color="danger"
              size="small"
              onClick={() => handleDelete(row)}
            >
              删除
            </Button>
          </Space>
        ),
      },
    ],
    [openEdit, handleDelete],
  );

  return (
    <div className="h-full overflow-auto bg-white p-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Form layout="inline">
          <FormControl
            control={control}
            name="status"
            label="状态"
            render={({ field }) => {
              return (
                <Select
                  {...field}
                  mode="multiple"
                  allowClear
                  style={{ width: 180 }}
                  maxTagCount="responsive"
                  options={[
                    { label: '已激活', value: 'active' },
                    { label: '处理中', value: 'processing' },
                    { label: '失败', value: 'failed' },
                  ]}
                />
              );
            }}
          ></FormControl>
          <FormControl
            control={control}
            name="name"
            label="人物名称"
            render={({ field }) => {
              return <Input {...field} value={field.value ?? ''} />;
            }}
          ></FormControl>
          <Button
            variant="filled"
            color="default"
            onClick={handleSubmit((values) => {
              queryMany({
                name: values.name ?? void 0,
                status: values.status ?? void 0,
              });
            })}
          >
            查询
          </Button>
        </Form>
        <div className="flex-auto" />
        <Button
          variant="solid"
          color="primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
        >
          新增人物
        </Button>
      </div>
      <Table
        rowKey={(record) => record.id}
        columns={columns}
        dataSource={data?.items ?? []}
        loading={isFetching}
        pagination={{
          current: pagination.page,
          pageSize: pagination.size,
          total: data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '30', '50', '100'],
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, size) => {
            changePageNumber(page, size);
          },
        }}
        scroll={{ x: 700 }}
      />
    </div>
  );
}
